import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button, Space, Tag, Table, Drawer, Form, Input, Select, AutoComplete,
  Typography, Tooltip, message, Popconfirm, Row, Col, Card,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  EyeOutlined, SearchOutlined, FileTextOutlined,
} from '@ant-design/icons';
import {
  getCropRawListApi, createCropRawApi, updateCropRawApi, deleteCropRawApi,
} from '../../api/cropRaw';
import { getCropNamesApi } from '../../api/crops';

const { Title, Text } = Typography;
const PRIMARY = '#1a6b2a';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar Islands', 'Chandigarh', 'Dadra & Nagar Haveli', 'Daman & Diu',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
].map((s) => ({ value: s, label: s }));

function apiErr(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

// ── Form Drawer ───────────────────────────────────────────────────────────────

function RawDataDrawer({ open, onClose, onSuccess, record }) {
  const [form]          = Form.useForm();
  const [saving,        setSaving]        = useState(false);
  const [cropNames,     setCropNames]     = useState([]);
  const [cropNamesLoad, setCropNamesLoad] = useState(false);
  const isEdit = !!record;

  const loadCropNames = useCallback(async (search = '') => {
    setCropNamesLoad(true);
    try {
      const res = await getCropNamesApi({ lang: 'en', crop: search });
      const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const seen = new Set();
      const unique = raw.filter((c) => {
        if (seen.has(c.common_id)) return false;
        seen.add(c.common_id);
        return true;
      });
      setCropNames(unique);
    } catch { setCropNames([]); }
    finally { setCropNamesLoad(false); }
  }, []);

  useEffect(() => {
    if (!open) { form.resetFields(); return; }
    loadCropNames();
    if (isEdit && record) {
      form.setFieldsValue({
        common_id: record.common_id,
        crop_name: record.crop_name,
        state:     record.state,
        raw_text:  record.raw_text,
        source:    record.source,
      });
    }
  }, [open, isEdit, record, form, loadCropNames]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (isEdit) {
        await updateCropRawApi(record.id, values);
        message.success('Record updated');
      } else {
        await createCropRawApi(values);
        message.success('Record created');
      }
      onSuccess();
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<Space><FileTextOutlined />{isEdit ? 'Edit Raw Data' : 'Add Raw Data'}</Space>}
      width={560}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="crop_name" label="Crop Name" rules={[{ required: true, message: 'Required' }]}>
              <AutoComplete
                options={cropNames.map((c) => ({ value: c.name, label: c.name, common_id: c.common_id }))}
                onSearch={(v) => loadCropNames(v)}
                onSelect={(val, opt) => {
                  form.setFieldValue('crop_name', val);
                  form.setFieldValue('common_id', opt.common_id);
                }}
                filterOption={false}
                placeholder="Type or select crop name…"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="common_id" label="Common ID (auto or manual)">
              <Input placeholder="e.g. wheat" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="state" label="State" rules={[{ required: true, message: 'Required' }]}>
              <Select
                showSearch
                allowClear
                options={INDIAN_STATES}
                placeholder="Select state"
                filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="source" label="Source" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="e.g. AAU, NAU, JAU, ICAR…" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="raw_text" label="Raw Text" rules={[{ required: true, message: 'Required' }]}>
          <Input.TextArea
            rows={6}
            placeholder="Paste the raw advisory or data text here…"
            showCount
            maxLength={5000}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────

function DetailDrawer({ open, onClose, record }) {
  if (!record) return null;
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<Space><EyeOutlined />Raw Data Detail</Space>}
      width={560}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Crop</Text>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{record.crop_name}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>{record.common_id}</Text>
          </Col>
          <Col span={12}>
            <Text type="secondary">State</Text>
            <div><Tag color="blue">{record.state}</Tag></div>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Source</Text>
            <div><Tag color="green">{record.source}</Tag></div>
          </Col>
          <Col span={12}>
            <Text type="secondary">ID</Text>
            <div style={{ fontWeight: 500 }}>#{record.id}</div>
          </Col>
        </Row>
        <div>
          <Text type="secondary">Raw Text</Text>
          <Card
            size="small"
            style={{ marginTop: 6, background: '#f9fafb', border: '1px solid #e8e8e8' }}
          >
            <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{record.raw_text}</Text>
          </Card>
        </div>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Created</Text>
            <div style={{ fontSize: 12 }}>{record.created_at}</div>
          </Col>
          <Col span={12}>
            <Text type="secondary">Updated</Text>
            <div style={{ fontSize: 12 }}>{record.updated_at}</div>
          </Col>
        </Row>
      </Space>
    </Drawer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminCropRaw() {
  const [data,       setData]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [cropFilter,  setCropFilter]  = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const [formOpen,   setFormOpen]   = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected,   setSelected]   = useState(null);

  const currentFilters = useRef({ crop: '', state: '' });

  const doFetch = useCallback(async (page, pageSize, filters) => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (filters.crop)  params.crop_name = filters.crop;
      if (filters.state) params.state     = filters.state;

      const res  = await getCropRawListApi(params);
      const body = res.data;
      const rows = Array.isArray(body.data) ? body.data : [];
      const pg   = body.pagination || {};

      setData(rows);
      setPagination((prev) => ({
        ...prev,
        current:  pg.current_page || page,
        pageSize: pg.per_page     || pageSize,
        total:    pg.total        || rows.length,
      }));
    } catch (err) {
      message.error(apiErr(err, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    doFetch(1, pagination.pageSize, { crop: cropFilter, state: stateFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    currentFilters.current = { crop: cropFilter, state: stateFilter };
    doFetch(1, pagination.pageSize, currentFilters.current);
  };

  const clearFilters = () => {
    setCropFilter('');
    setStateFilter('');
    currentFilters.current = { crop: '', state: '' };
    doFetch(1, pagination.pageSize, { crop: '', state: '' });
  };

  const handleTableChange = (pg) => {
    doFetch(pg.current, pg.pageSize, currentFilters.current);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCropRawApi(id);
      message.success('Deleted');
      doFetch(pagination.current, pagination.pageSize, currentFilters.current);
    } catch (err) {
      message.error(apiErr(err, 'Delete failed'));
    }
  };

  const openAdd = () => { setSelected(null); setFormOpen(true); };
  const openEdit = (rec) => { setSelected(rec); setFormOpen(true); };
  const openDetail = (rec) => { setSelected(rec); setDetailOpen(true); };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 64,
      render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>#{v}</Text>,
    },
    {
      title: 'Crop',
      dataIndex: 'crop_name',
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{row.common_id}</Text>
        </Space>
      ),
    },
    {
      title: 'State',
      dataIndex: 'state',
      width: 150,
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 100,
      render: (v) => <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Raw Text',
      dataIndex: 'raw_text',
      ellipsis: true,
      render: (v) => (
        <Tooltip title={v} overlayStyle={{ maxWidth: 500 }}>
          <Text style={{ fontSize: 12, color: '#555' }}>
            {v?.length > 100 ? v.slice(0, 100) + '…' : v}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      width: 160,
      render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Space>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(row)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            onConfirm={() => handleDelete(row.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <Space align="center">
          <FileTextOutlined style={{ fontSize: 22, color: PRIMARY }} />
          <Title level={4} style={{ margin: 0 }}>Crop Raw Data</Title>
          <Tag color="default" style={{ marginLeft: 4 }}>{pagination.total} records</Tag>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => doFetch(pagination.current, pagination.pageSize, currentFilters.current)}
            loading={loading}
          >
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add Raw Data
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col xs={24} sm={8}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bbb' }} />}
              placeholder="Filter by crop name…"
              value={cropFilter}
              onChange={(e) => setCropFilter(e.target.value)}
              onPressEnter={applyFilters}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              allowClear
              showSearch
              style={{ width: '100%' }}
              placeholder="Filter by state…"
              value={stateFilter || undefined}
              onChange={(v) => setStateFilter(v || '')}
              options={INDIAN_STATES}
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>
                Search
              </Button>
              {(cropFilter || stateFilter) && (
                <Button onClick={clearFilters}>Clear</Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{
          current:      pagination.current,
          pageSize:     pagination.pageSize,
          total:        pagination.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, [from, to]) => `${from}-${to} of ${total}`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 900 }}
      />

      {/* Add / Edit Drawer */}
      <RawDataDrawer
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => doFetch(pagination.current, pagination.pageSize, currentFilters.current)}
        record={selected}
      />

      {/* Detail Drawer */}
      <DetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        record={selected}
      />
    </div>
  );
}
